import { Suspense, useEffect, useState } from 'react'
import { observer } from 'mobx-react-lite'
import { Outlet, useNavigate } from 'react-router-dom'
import { AppShell } from 'app/components/AppShell'
import { ConfirmationServiceProvider } from 'app/components/ConfirmationContext'
import { GlobalSearch } from 'app/components/global-search/GlobalSearch'
import { MessageAlertStack } from 'app/components/inbox/MessageAlertStack'
import { Modal } from 'app/components/Modal'
import { SideBarV2 } from 'app/components/sidebar/SideBarV2'
import { SideBarV3 } from 'app/components/sidebar/SideBarV3'
import { SubnavProvider } from 'app/components/SideBarContext'
import { RailFrame } from 'app/components/layout/RailFrame'
import { useEventStream } from 'app/hooks/useEventStream'
import { useFeatureToggle } from 'app/hooks/useFeatureToggle'
import { SidebarCountsProvider } from 'app/hooks/useSidebarCounts'
import { UserPermissionsProvider } from 'app/hooks/useUserPermission'
import { useTableColumnLinesSync } from 'app/hooks/useTableColumnLines'
import { useThemeSync } from 'app/hooks/useThemeSync'
import Spinner from 'app/components/Spinner'
import { WorkspaceGate } from 'app/components/WorkspaceGate'
import { fullstoryEnabled } from 'app/fullstory'
import { intercomEnabled } from 'app/intercom'
import { eventRouter, type EventRouterPayload } from 'app/lib/eventRouter'
import {
  connect as connectEventStream,
  disconnect as disconnectEventStream,
  type IncomingCall,
} from 'app/lib/eventStream'
import { getModalState, OptionsType } from 'app/lib/modal'
import { useAuthStore, useFeatureToggleStore, useIncomingCallStore, useRootStore } from 'app/stores/StoreContext'
import { bg } from 'lib/background'
import { debug, log, logError, logWarn } from 'lib/logging'
import { sleep } from 'lib/sleep'

const MAX_IDLE_TIME = 30 * 60 * 1000 + 50

// How long to wait for a third-party script tag to define its global before
// giving up. Bounded so a blocked/missing script logs instead of polling forever.
const SCRIPT_LOAD_TIMEOUT = 30 * 1000

// Tags the Intercom contact so support can tell real traffic apart from the
// other deploys. app.trackable.io is `prod`, every other trackable.io host
// reports its own subdomain (`dev`, `next.app`), anything else is `local`.
function currentEnvironment() {
  const { hostname } = document.location
  if (hostname === `app.trackable.io`) return `prod`
  if (hostname.endsWith(`.trackable.io`)) return hostname.replace(`.trackable.io`, ``)
  return `local`
}

async function waitForGlobal(isReady: () => boolean) {
  const deadline = Date.now() + SCRIPT_LOAD_TIMEOUT
  while (!isReady()) {
    if (Date.now() >= deadline) return false
    await sleep(1000)
  }
  return true
}

// Fallback for the content-area Suspense boundary. Fills the outlet region
// (not the whole viewport) so the sidebar/header remain visible while a
// lazily-loaded route chunk is fetched.
function OutletSpinner() {
  return (
    <div className="flex h-full w-full items-center justify-center">
      <Spinner size={10} />
    </div>
  )
}

export const AppLayout = observer(function AppLayoutInner() {
  const auth = useAuthStore()
  const featureToggleStore = useFeatureToggleStore()
  const incomingCallStore = useIncomingCallStore()
  const groupedNavigationEnabled = useFeatureToggle(`grouped-navigation`)
  const rootStore = useRootStore()
  useTableColumnLinesSync()
  useThemeSync()
  const { user, loading: fetching } = auth
  const navigate = useNavigate()

  useEventStream(`incoming-call`, (event) => {
    const call = event.payload as IncomingCall
    incomingCallStore.add(call)
    log(`Incoming call`, call)
  })

  useEventStream(`event`, (event) => eventRouter(rootStore, event.payload as EventRouterPayload))

  // Fetch current user on mount
  useEffect(() => {
    bg(auth.fetchMe())
  }, [])

  // Front-load the user's feature toggles once authenticated, in a single
  // request, so `useFeatureToggle` reads synchronously without flicker. Uses
  // featureTogglesByName, which is accessible to any user (the list query is
  // super-only and 403s for everyone else).
  useEffect(() => {
    if (user) bg(() => featureToggleStore.load())
  }, [user])

  // Live event stream. One same-origin socket per authenticated session, scoped
  // to the active workspace. Cleanup (and the workspaceId dep) tears it down on
  // logout or a workspace switch so events for the old workspace can never leak
  // into the new one.
  useEffect(() => {
    const workspaceId = user?.workspaceId
    incomingCallStore.setWorkspace(workspaceId)
    if (!workspaceId) return

    connectEventStream(workspaceId)
    bg(() => incomingCallStore.load())
    return () => {
      disconnectEventStream()
      incomingCallStore.setWorkspace(undefined)
    }
  }, [user?.workspaceId])

  // Templates once lived in each browser. Move this browser's copies for the open workspace
  // onto the server; it makes no request unless the browser still holds some.
  useEffect(() => {
    const workspaceId = user?.workspaceId
    if (!workspaceId) return
    bg(() => rootStore.smsTemplates.importBrowserTemplates(workspaceId))
    bg(() => rootStore.taskTemplates.importBrowserTemplates(workspaceId))
  }, [user?.workspaceId])

  // Auth redirect
  useEffect(() => {
    if (!fetching && !user) {
      const returnTo = encodeURIComponent(window.location.pathname + window.location.search)
      window.location.href = `/login?redirectTo=${returnTo}`
    }
  }, [fetching, user])

  // Idle logout check
  useEffect(() => {
    const logOutInterval = setInterval(() => {
      auth
        .fetchMe({ background: true })
        .then(() => {
          if (!auth.user) {
            debug(`logout`, `logging user out due to inactivity`)
            navigate(
              `/login?redirectTo=${encodeURIComponent(window.location.href.replace(window.location.origin, ``))}`,
            )
          } else {
            debug(`logout`, `user still active`)
          }
        })
        .catch(() => logError(`error calling fetchMe`))
    }, MAX_IDLE_TIME)
    return () => clearInterval(logOutInterval)
  }, [])

  // Intercom + Fullstory
  useEffect(() => {
    if (!user) return
    if (intercomEnabled()) {
      bg(async () => {
        if (!(await waitForGlobal(() => window.Intercom != null))) {
          logWarn(`intercom`, `widget script never loaded, skipping boot`)
          return
        }
        try {
          window.Intercom?.(`boot`, {
            app_id: `m8f1sces`,
            // Keep in sync with intercomSettings in app/intercom.js: boot
            // replaces the settings, so omitting it here brings the launcher back.
            hide_default_launcher: true,
            email: user.email,
            org: user.orgId,
            user_id: user.id,
            created_at: Date.now(),
            environment: currentEnvironment(),
            workspaceId: user.workspaceId,
          })
        } catch (err) {
          logWarn(`intercom`, `failed to boot`, err)
        }
      })
    }
    if (fullstoryEnabled()) {
      bg(async () => {
        if (!(await waitForGlobal(() => window.FS != null))) {
          logWarn(`fullstory`, `script never loaded, skipping identify`)
          return
        }
        try {
          window.FS?.identify(user.id, {
            displayName: user.name,
            email: user.email,
            position: user.position,
            title: user.jobTitle,
            org: user.orgId,
            workspaceId: user.workspaceId,
          })
        } catch (err) {
          logWarn(`fullstory`, `failed to boot`, err)
        }
      })
    }
  }, [user])

  // Modal state (same pattern as __app.tsx)
  const [modalOpen, setModalOpen] = useState(false)
  const [nestedModalOpen, setNestedModalOpen] = useState(false)
  const [modalContent, setModalContent] = useState<React.ReactElement | undefined | null>()
  const [options, setOptions] = useState<OptionsType>()
  const [nestedModalContent, setNestedModalContent] = useState<React.ReactElement | undefined | null>()
  const [nestedOptions, setNestedOptions] = useState<OptionsType>()
  const [isMinimized, setIsMinimized] = useState(false)

  useEffect(() => {
    const state = getModalState()
    state.setModalOpen = setModalOpen
    state.setNestedModalOpen = setNestedModalOpen
    state.setContent = setModalContent
    state.setOptions = setOptions
    state.setTitle = (title: string | React.ReactElement) => {
      setOptions((prev) => (prev ? { ...prev, title } : undefined))
    }
    state.setNestedTitle = (title: string | React.ReactElement) => {
      setNestedOptions((prev) => (prev ? { ...prev, title } : undefined))
    }
    state.setNestedContent = setNestedModalContent
    state.setNestedOptions = setNestedOptions
    state.setMainMinimizedState = (value: boolean) => setIsMinimized(value)
  }, [])

  useEffect(() => {
    const state = getModalState()
    state.isModalOpen = modalOpen
    state.isNestedModalOpen = nestedModalOpen
  }, [modalOpen, nestedModalOpen])

  useEffect(() => {
    if (modalContent != null) setModalOpen(true)
    else setModalOpen(false)
  }, [modalContent])

  useEffect(() => {
    if (nestedModalContent != null) setNestedModalOpen(true)
    else setNestedModalOpen(false)
  }, [nestedModalContent])

  if (fetching) {
    return (
      <div className="fixed inset-0 flex items-center justify-center">
        <Spinner size={10} />
      </div>
    )
  }

  if (!user) return null

  return (
    <UserPermissionsProvider>
      {/* `ConfirmationServiceProvider` wraps everything so `useConfirmation()`
          calls anywhere in the tree resolve to a real `confirm()` function
          (and its inline Radix dialog renders). Without it, every consumer
          got the context default `Promise.reject`, which silently broke
          delete/confirm flows. */}
      <ConfirmationServiceProvider>
        <SidebarCountsProvider>
          <SubnavProvider>
            <AppShell sidebar={groupedNavigationEnabled ? <SideBarV3 /> : <SideBarV2 />}>
              <WorkspaceGate>
                {/* Scope route-chunk suspense to the content area so the
                    sidebar/header stay mounted while a lazily-loaded page
                    chunk is fetched on first visit. Without this boundary,
                    the top-level Suspense in App.tsx replaces the whole
                    screen with a full-page spinner (white flash). */}
                <Suspense fallback={<OutletSpinner />}>
                  <RailFrame>
                    <Outlet />
                  </RailFrame>
                </Suspense>
              </WorkspaceGate>
            </AppShell>
          </SubnavProvider>
          {/* Fixed-position overlay, so it lives outside AppShell's overflow-hidden
              main area and survives route changes. */}
          <MessageAlertStack />
          <GlobalSearch />
        </SidebarCountsProvider>
        <Modal
          {...options!}
          children={modalContent}
          open={modalOpen}
          onClose={() => {
            options?.onClose?.()
          }}
          nestedModal={
            <Modal
              {...nestedOptions!}
              open={nestedModalOpen}
              children={nestedModalContent}
              onClose={() => {
                nestedOptions?.onClose?.()
              }}
              isNested
            />
          }
          isMinimized={isMinimized}
        />
      </ConfirmationServiceProvider>
    </UserPermissionsProvider>
  )
})

export default AppLayout
